import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Location autocomplete using OpenStreetMap Nominatim (free, no keys, MIT-licensed data).
 * Debounced 350ms, results limited to 6. Falls back to plain text if the network dies.
 * Nominatim usage policy: <=1 req/sec, valid user-agent — we send a simple one and debounce.
 */
export default function LocationAutocomplete({ value, onChange, onCoords, placeholder, testId, required }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  // Keep local input in sync when the parent value changes externally (e.g. edit-load)
  useEffect(() => { setQuery(value || ""); }, [value]);

  const runSearch = (q) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=${encodeURIComponent(q)}`, {
      signal: controller.signal,
      headers: { "Accept-Language": "en" },
    })
      .then((r) => r.json())
      .then((data) => {
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
      })
      .catch(() => { /* aborted or offline — silent */ })
      .finally(() => setLoading(false));
  };

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);              // always mirror plain text upward
    clearTimeout(timerRef.current);
    if (v.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(() => runSearch(v), 350);
  };

  const pick = (r) => {
    onChange(r.display_name);
    if (onCoords && r.lat && r.lon) onCoords(parseFloat(r.lat), parseFloat(r.lon));
    setQuery(r.display_name);
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <Input
          value={query}
          onChange={onInput}
          onFocus={() => { if (results.length) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder || "Search a venue, street or city"}
          data-testid={testId}
          required={required}
          className="pl-9 pr-9 h-11"
          autoComplete="street-address"
        />
        {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-72 overflow-auto" data-testid={`${testId || "loc"}-results`}>
          {results.map((r) => (
            <li key={r.place_id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                className="w-full text-left px-3 py-2 hover:bg-zinc-50 text-sm border-b border-zinc-100 last:border-b-0"
                data-testid={`${testId || "loc"}-result-${r.place_id}`}
              >
                <div className="font-medium">{r.display_name.split(",")[0]}</div>
                <div className="text-xs text-zinc-500 truncate">{r.display_name}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
